import { memo, type ReactNode, Suspense, useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';

import { Layout as AntLayout } from 'antd';

import {
    PatternStudioMenu,
    PatternStudioSider,
    PatternStudioSiderContext,
} from '@/widgets/layout';
import { PatternWatchlistAsDrawer } from '@/widgets/pattern-watchlist';

import { patternFilterReducer } from '@/features/pattern-filter';
import { patternLabReducer } from '@/features/pattern-lab';

import { patternReducer } from '@/entities/pattern';
import { patternLiveReducer } from '@/entities/pattern-live';
import { patternWatchlistReducer } from '@/entities/pattern-watchlist';

import {
    useDesktopMediaQuery,
    useMobileMediaQuery,
    useTabletMediaQuery,
} from '@/shared/lib/hooks';
import {
    type ReducersList,
    useDynamicReducersLoader,
} from '@/shared/lib/store';
import { PageLoader } from '@/shared/ui/page-loader';

import cls from './pattern-studio-layout.module.css';

interface PatternStudioLayoutProps {
    headerSlot?: ReactNode;
    isLoading?: boolean;
}

const reducers: ReducersList = {
    pattern: patternReducer,
    patternFilter: patternFilterReducer,
    patternWatchlist: patternWatchlistReducer,
    patternLive: patternLiveReducer,
    patternLab: patternLabReducer,
};

export const PatternStudioLayout = memo((props: PatternStudioLayoutProps) => {
    const { headerSlot = <PatternStudioMenu />, isLoading = false } = props;

    const isDesktop = useDesktopMediaQuery();
    const isTablet = useTabletMediaQuery();
    const isMobile = useMobileMediaQuery();

    const [isCollapsed, setIsCollapsed] = useState(true);

    useDynamicReducersLoader({
        reducers,
        needRemoveAfterUnmount: false,
    });

    const content = useMemo(
        () => (
            <AntLayout className={cls.layout}>
                {headerSlot}
                <AntLayout.Content className={cls.content}>
                    <Suspense fallback={<PageLoader />}>
                        {isLoading ? <PageLoader /> : <Outlet />}
                    </Suspense>
                </AntLayout.Content>
            </AntLayout>
        ),
        [headerSlot, isLoading],
    );

    const siderContextValue = useMemo(
        () => ({ isCollapsed, setIsCollapsed }),
        [isCollapsed],
    );

    return (
        <PatternStudioSiderContext.Provider value={siderContextValue}>
            <AntLayout className={cls.layout}>
                {isMobile && <PatternWatchlistAsDrawer />}
                {content}
                {(isDesktop || isTablet) && (
                    <div className={cls.siderContainer}>
                        <PatternStudioSider
                            isCollapsed={isCollapsed}
                            setIsCollapsed={setIsCollapsed}
                        />
                    </div>
                )}
            </AntLayout>
        </PatternStudioSiderContext.Provider>
    );
});

PatternStudioLayout.displayName = 'PatternStudioLayout';
